<template>
  <div class="input-select-sheet">
    <label v-if="title" class="custom-label" :name="'label-' + name">
      {{ title }}
      <span v-if="isRequired" class="text-danger">*</span>
    </label>

    <button
      type="button"
      class="input-select-sheet__trigger"
      :class="{ 'is-error': isValidate || (v && v.$error) }"
      :disabled="disabled"
      @click="openSheet"
      :data-test="$attrs['data-test']"
    >
      <div class="input-select-sheet__display">
        <img
          v-if="selectedFlag"
          :src="selectedFlag"
          alt="flag"
          class="input-select-sheet__flag"
        />
        <span v-if="selectedLabel">{{ selectedLabel }}</span>
        <span v-else class="input-select-sheet__placeholder">
          {{
            customPlaceholder
              ? placeholder
              : "-- " + (placeholder || $t("default_select_validate")) + " --"
          }}
        </span>
      </div>
      <font-awesome-icon
        v-if="!disabled"
        :icon="['fas', 'chevron-down']"
        class="input-select-sheet__caret"
      />
    </button>

    <template v-if="eachValid">
      <div v-if="eachValid.$error && v && v.$error">
        <span class="text-error" v-if="v.required.$invalid">{{
          validateText || $t("default_select_validate")
        }}</span>
        <span class="text-error" v-else-if="!v.required">{{
          validateText || $t("default_select_validate")
        }}</span>
        <span
          class="text-error"
          v-else-if="v.minValue && v.minValue.$invalid"
          >{{ options[0][textField || "text"] }}</span
        >
        <span
          class="text-error"
          v-if="v.valueDefault && v.valueDefault.$invalid"
          >{{ options[0][textField || "text"] }}</span
        >
      </div>
    </template>
    <template v-else>
      <div v-if="v && v.$error">
        <span class="text-error" v-if="v.required.$invalid || !v.required">{{
          validateText || $t("default_select_validate")
        }}</span>
        <span
          class="text-error"
          v-else-if="v.minValue && v.minValue.$invalid"
          >{{ options[0][textField || "text"] }}</span
        >
        <span
          class="text-error"
          v-if="v.valueDefault && v.valueDefault.$invalid"
          >{{ options[0][textField || "text"] }}</span
        >
      </div>
    </template>
  </div>
</template>

<script>
import { markRaw } from "vue";
import { useDialog } from "@/services/dialogService";
import SelectSheetDialogContent from "@/components/dialog-content/SelectSheetDialogContent.vue";

const RawSelectSheetDialogContent = markRaw(SelectSheetDialogContent);

export default {
  name: "InputSelectSheet",
  setup() {
    const dialog = useDialog();
    return { dialog };
  },
  props: {
    title: { required: false },
    name: { required: false },
    options: { required: true, default: () => [] },
    value: { required: false },
    isRequired: { required: false, type: Boolean },
    isValidate: { required: false },
    v: { required: false },
    valueField: { required: false, default: "id" },
    textField: { required: false, default: "name" },
    disabled: { required: false },
    placeholder: { required: false },
    validateText: { required: false },
    eachValid: { required: false, type: Object, default: null },
    formType: { required: false, default: false, type: Boolean },
    customPlaceholder: { required: false, type: Boolean, default: false },
    searchable: { required: false, type: Boolean, default: true },
    searchPlaceholder: { required: false, type: String, default: "" }
  },
  emits: ["change", "update:value", "setOtherValue", "deleteOtherValue"],
  computed: {
    selectedOption() {
      if (this.value == null || this.value === "") return null;
      return (this.options || []).find(o => {
        if (o !== null && typeof o === "object") {
          return o[this.valueField] == this.value;
        }
        return o == this.value;
      });
    },
    selectedLabel() {
      const o = this.selectedOption;
      if (o == null) return null;
      if (typeof o === "object") return o[this.textField] ?? o[this.valueField];
      return String(o);
    },
    selectedFlag() {
      const o = this.selectedOption;
      return o && typeof o === "object" ? o.flag || null : null;
    },
    shallowOptions() {
      const opts = this.options || [];
      const valueField = this.valueField;
      const textField = this.textField;
      return opts.map(o => {
        if (o !== null && typeof o === "object") {
          return {
            [valueField]: o[valueField],
            [textField]: o[textField],
            flag: o.flag,
            sort_order: o.sort_order || 0,
            choice_type_id: o.choice_type_id
          };
        }
        return o;
      });
    }
  },
  created() {
    this.setOtherValue();
  },
  mounted() {
    this.setDefault();
  },
  methods: {
    async openSheet() {
      if (this.disabled) return;
      const plainOptions = markRaw(
        JSON.parse(JSON.stringify(this.shallowOptions))
      );
      const result = await this.dialog.showCustom(RawSelectSheetDialogContent, {
        title: this.title || this.placeholder || "",
        options: plainOptions,
        value: this.value,
        valueField: this.valueField,
        textField: this.textField,
        searchable: this.searchable,
        searchPlaceholder: this.searchPlaceholder,
        dataTestPrefix: this.$attrs["data-test"] || this.name || "select-sheet"
      });
      if (result?.action === "confirm") {
        this.updateModelValue(result.value);
      }
    },
    updateModelValue(val) {
      const find = this.options.find(el => el.name == val);
      if (find && find.choice_type_id == 3) this.$emit("deleteOtherValue");
      this.$emit("change", val);
      this.$emit("update:value", val);
    },
    setOtherValue() {
      if (!this.formType) return;
      if (this.value && this.options.length > 0) {
        const value = this.options.find(el => el.name == this.value);
        if (!value) {
          const other = this.options.find(el => el.choice_type_id == 3);
          if (other) {
            this.$emit("setOtherValue", this.value);
            this.$emit("update:value", other.name);
          }
        }
      }
    },
    setDefault() {
      if (!this.formType) return;
      if (this.value) return;
      this.$nextTick(() => {
        const check = this.options.find(el => el.isDefault == 1);
        if (check) this.$emit("update:value", check.name);
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.input-select-sheet {
  position: relative;
}

.custom-label {
  font-size: var(--font-body-lg);
  margin-bottom: 5px;
  display: inline-block;
}

.input-select-sheet__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 45px;
  padding: 8px 14px;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  background: #fff;
  color: #2b2b2b;
  font-size: var(--font-body);
  text-align: left;
  cursor: pointer;

  &:disabled {
    background: var(--disable-color);
    color: #9a9a9a;
    cursor: not-allowed;
  }

  &.is-error {
    border-color: #e00012;
  }
}

.input-select-sheet__display {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.input-select-sheet__placeholder {
  color: #9a9a9a;
}

.input-select-sheet__flag {
  width: 20px;
  height: 14px;
  flex-shrink: 0;
}

.input-select-sheet__caret {
  color: #9a9a9a;
  flex-shrink: 0;
}

.text-error {
  color: #e00012;
  font-size: var(--font-helper);
}
</style>
